import { Component, computed, inject, signal, DestroyRef, OnInit, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CaseService, SearchCriteria } from '../../services/case-service/case.service';
import { LookUpService } from '../../services/lookup-service/lookup.service';
import { SearchableSelect } from '../../shared-components/searchable-select/searchable-select';
import { ActionService } from '../../services/action-service/action.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { copyToClipboard } from '../../shared-components/utils/copy-to-clipboard.util';
import { NumericOnlyDirective } from '../../shared-components/directives/numeric-only.directive';
import { WarningTooltip } from '../../shared-components/warning-tooltip/warning-tooltip';
import { ConfigService } from '../../core/config.service';

type NumericField = 'caseNo' | 'streetNo' | 'apn';

@Component({
  selector: 'app-search-cases',
  imports: [CommonModule, FormsModule, SearchableSelect, NumericOnlyDirective, WarningTooltip],
  templateUrl: `./search-cases.html`,
  styleUrls: [`./search-cases.scss`],
})
export class SearchCasesComponent implements OnInit, OnDestroy {
  private readonly caseService = inject(CaseService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly tooltipTexts = inject(ConfigService).getTooltipTexts();

  protected readonly criteria = this.caseService.searchCriteria;
  protected readonly types = this.caseService.types;
  protected readonly actionService = inject(ActionService);
  protected readonly lookUpService = inject(LookUpService);

  protected isCopied = signal(false);
  protected readonly numericHint = signal<NumericField | null>(null);

  // Street types still hit the API, so keystrokes are debounced/deduped
  // before reaching LookUpService (which cancels in-flight requests via switchMap).
  // Directions are filtered locally and instantly, so no debounce is needed.
  private readonly streetTypeSearch$ = this.createDebouncedSearch((term) =>
    this.lookUpService.searchStreetTypes(term),
  );

  private pendingCopyTimeout: ReturnType<typeof setTimeout> | null = null;
  private numericHintTimeout: ReturnType<typeof setTimeout> | null = null;

  protected readonly hasNoSearchValues = computed(() =>
    Object.values(this.criteria()).every((v) => !v?.trim()),
  );

  ngOnInit(): void {
    this.resetLookupSearches();
  }

  ngOnDestroy(): void {
    if (this.pendingCopyTimeout !== null) {
      clearTimeout(this.pendingCopyTimeout);
      this.pendingCopyTimeout = null;
    }
    if (this.numericHintTimeout !== null) {
      clearTimeout(this.numericHintTimeout);
      this.numericHintTimeout = null;
    }

    this.streetTypeSearch$.complete();
  }

  showNumericHint(field: NumericField): void {
    this.numericHint.set(field);
    if (this.numericHintTimeout !== null) {
      clearTimeout(this.numericHintTimeout);
    }
    this.numericHintTimeout = setTimeout(() => {
      this.numericHint.set(null);
      this.numericHintTimeout = null;
    }, 2000);
  }

  protected numericTooltip(maxLength: number): string {
    return this.tooltipTexts.numericOnly.replace('{maxLength}', String(maxLength));
  }

  private createDebouncedSearch(fn: (term: string) => void): Subject<string> {
    const subject = new Subject<string>();
    subject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => fn(term));
    return subject;
  }

  private resetLookupSearches(): void {
    this.streetTypeSearch$.next('');
    this.lookUpService.searchDirections('');
  }

  onStreetSearchChange(term: string) {
    this.streetTypeSearch$.next(term);
  }

  onStreetScrollToEnd(): void {
    this.lookUpService.loadMoreStreetTypes();
  }

  onDirectionSearchChange(term: string) {
    this.lookUpService.searchDirections(term);
  }

  updateField(key: keyof SearchCriteria, value: string) {
    this.caseService.updateCriteria(key, value);
  }

  copyCaseNo() {
    if (copyToClipboard(this.criteria().caseNo)) {
      this.isCopied.set(true);

      if (this.pendingCopyTimeout !== null) {
        clearTimeout(this.pendingCopyTimeout);
      }

      this.pendingCopyTimeout = setTimeout(() => {
        this.isCopied.set(false);
        this.pendingCopyTimeout = null;
      }, 2000);
    }
  }

  search() {
    this.actionService.reset();
    this.caseService.performSearch();
    this.resetLookupSearches();
  }

  reset() {
    this.caseService.resetSearch();
    this.actionService.reset();
    this.resetLookupSearches();
  }
}